import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaLinkedinIn, FaWhatsapp } from "react-icons/fa";
import { IoIosMail } from "react-icons/io";
import emailjs from "@emailjs/browser";

const Footer = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    message: "",
  });
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const emailValidation = (email) =>
    String(email)
      .toLowerCase()
      .match(/^[\w.-]+@[\w.-]+\.\w{2,3}$/);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrMsg("");
    setSuccessMsg("");
  };

  const validateForm = () => {
    const { name, mobile, email, message } = formData;
    if (!name) return "Name is required.";
    if (!mobile) return "Phone number is required.";
    if (!email) return "Email is required.";
    if (!emailValidation(email)) return "Please enter a valid email.";
    if (!message) return "Message is required.";
    return "";
  };

  const handleSend = (e) => {
    e.preventDefault();
    const error = validateForm();
    if (error) {
      setErrMsg(error);
      return;
    }
    setErrMsg("");
    setLoading(true);
    handleSubmit();
  };

  const handleSubmit = async () => {
    try {
      await emailjs.send(
        "service_400bmzn",
        "template_yn5mfyk",
        formData,
        "2cxub4iS9xRO6VTye"
      );
      setFormData({ name: "", mobile: "", email: "", message: "" });
      setSuccessMsg("Message sent successfully.");
    } catch (err) {
      console.error("Error sending email:", err);
      setErrMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const links = [
    {
      href: "https://wa.me/+917000385306",
      icon: FaWhatsapp,
      label: "WhatsApp",
    },
    {
      href: "https://mail.google.com/mail/?view=cm&fs=1&to=imdev.aditya@gmail.com",
      icon: IoIosMail,
      label: "Email",
    },
    {
      href: "https://www.linkedin.com/in/imdev-aditya",
      icon: FaLinkedinIn,
      label: "LinkedIn",
    },
  ];

  return (
    <footer id="contact" className="relative w-full border-t border-white/10 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-80 h-24 bg-[radial-gradient(ellipse_60%_40%,rgba(255,1,79,0.04),transparent_70%)] blur-xl pointer-events-none" />
      <div className="relative max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-10 sm:py-12">
        {/* Header — aligned with other sections */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-gray-500 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-designColor" />
            Contact
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight mt-3">
            Get in touch
          </h2>
          <span className="mt-2 block h-px w-10 bg-gradient-to-r from-designColor/50 to-transparent rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
          {/* Left: contact info + social */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
            className="lg:col-span-5"
          >
            <div className="group rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4 sm:p-5 transition-all duration-300 hover:border-white/15 hover:shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
              <div className="flex items-baseline gap-2 mb-2">
                <h3 className="text-base font-semibold text-white group-hover:text-designColor transition-colors duration-200">Aditya Shrivastava</h3>
                <span className="text-[10px] font-medium text-designColor uppercase tracking-wider">Full Stack</span>
              </div>
              <div className="space-y-2 text-sm">
                <a href="tel:+917000385036" className="flex items-center gap-2 text-gray-400 hover:text-designColor transition-colors duration-200">
                  <span className="text-gray-600">Phone</span>
                  <span>+91 7000385036</span>
                </a>
                <a href="mailto:imdev.aditya@gmail.com" className="flex items-center gap-2 text-gray-400 hover:text-designColor transition-colors duration-200">
                  <span className="text-gray-600">Email</span>
                  <span>imdev.aditya@gmail.com</span>
                </a>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                {links.map(({ href, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-gray-400 hover:border-designColor/50 hover:bg-designColor/10 hover:text-designColor transition-all duration-200"
                  >
                    <Icon size={14} />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.4, delay: 0.05, ease: [0.25, 1, 0.5, 1] }}
            className="lg:col-span-7"
          >
            <div className="rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4 sm:p-5 transition-all duration-300 hover:border-white/15 hover:shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
              {errMsg && (
                <p className="mb-3 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                  {errMsg}
                </p>
              )}
              {successMsg && (
                <p className="mb-3 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                  {successMsg}
                </p>
              )}
              <form onSubmit={handleSend} className="flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {["name", "mobile", "email"].map((field) => (
                    <div key={field} className="flex flex-col gap-1">
                      <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                        {field === "mobile" ? "Phone" : field}
                      </label>
                      <input
                        name={field}
                        value={formData[field]}
                        onChange={handleChange}
                        type={field === "email" ? "email" : "text"}
                        placeholder={field === "mobile" ? "+91 …" : field === "email" ? "you@example.com" : "Your name"}
                        className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-designColor/50 focus:ring-1 focus:ring-designColor/20 transition-all duration-200"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                  <div className="flex-1 flex flex-col gap-1">
                    <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Hi, I'd like to…"
                      rows={2}
                      className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-designColor/50 focus:ring-1 focus:ring-designColor/20 resize-none transition-all duration-200 min-h-[72px]"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-shrink-0 h-9 px-5 rounded-lg bg-designColor/90 hover:bg-designColor text-white text-xs font-medium uppercase tracking-wider transition-all duration-200 hover:shadow-[0_0_20px_rgba(255,1,79,0.25)] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {loading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      "Send"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-6 pt-4 border-t border-white/5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
          <p>© {new Date().getFullYear()} Aditya Shrivastava</p>
          <div className="flex gap-4">
            {links.map(({ href, icon: Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="text-gray-500 hover:text-designColor transition-colors duration-200"
              >
                <Icon size={14} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
